export interface FeaturePermission {
  key: string;
  label: string;
  group: string;
  path: string;
  permission: string;
  aliases?: string[];
}

export const FEATURE_PERMISSIONS: FeaturePermission[] = [
  { key: 'conversations', label: '会话存档', group: '会话管理', path: '/conversations', permission: 'feature:conversations', aliases: ['conversation:read', 'conversation:view'] },
  { key: 'conversation-inherit', label: '会话继承', group: '会话继承', path: '/conversation-inherit', permission: 'feature:conversation-inherit', aliases: ['conversation:read', 'conversation:view'] },
  { key: 'conversation-inherit-history', label: '历史继承', group: '会话继承', path: '/conversation-inherit/history', permission: 'feature:conversation-inherit-history' },
  { key: 'conversation-export', label: '会话导出', group: '会话管理', path: '/conversation-export', permission: 'feature:conversation-export', aliases: ['conversation:export'] },
  { key: 'file-recording', label: '文件录音', group: '会话管理', path: '/file-recording', permission: 'feature:file-recording', aliases: ['conversation:read', 'conversation:view'] },
  { key: 'rules', label: '风险设置', group: '风险审计', path: '/rules', permission: 'feature:rules', aliases: ['rule:read', 'rule:view', 'feature:alerts', 'alert:read', 'alert:view'] },
  { key: 'alerts', label: '风险数据', group: '风险审计', path: '/alerts', permission: 'feature:alerts', aliases: ['alert:read', 'alert:view'] },
  { key: 'risk-records', label: '风险记录', group: '风险审计', path: '/risk-records', permission: 'feature:customer-loss', aliases: ['feature:archive-rejections', 'feature:alerts', 'alert:read', 'alert:view'] },
  { key: 'reports', label: '统计报表', group: '统计报表', path: '/reports', permission: 'feature:reports', aliases: ['report:read', 'report:view'] },
  { key: 'report-contact-customer-detail', label: '联系客户明细', group: '统计报表', path: '/reports/contact-customer-detail', permission: 'feature:reports', aliases: ['report:read', 'report:view'] },
  { key: 'report-group-chat-detail', label: '群聊数据明细', group: '统计报表', path: '/reports/group-chat-detail', permission: 'feature:reports', aliases: ['report:read', 'report:view'] },
  { key: 'report-employee-ranking', label: '员工排行', group: '统计报表', path: '/reports/employee-ranking', permission: 'feature:reports', aliases: ['report:read', 'report:view'] },
  { key: 'report-department-ranking', label: '部门排行', group: '统计报表', path: '/reports/department-ranking', permission: 'feature:reports', aliases: ['report:read', 'report:view'] },
  { key: 'departments', label: '组织架构', group: '基础配置', path: '/departments', permission: 'feature:departments', aliases: ['department:read', 'department:view'] },
  { key: 'agents', label: '应用配置', group: '基础配置', path: '/agents', permission: 'feature:agents', aliases: ['agent:read', 'agent:view'] },
  { key: 'permissions', label: '权限管理', group: '系统设置', path: '/settings/permissions', permission: 'feature:permissions', aliases: ['settings:write', 'settings:update', 'role:read', 'role:view'] },
  { key: 'accounts', label: '账号管理', group: '系统设置', path: '/settings/accounts', permission: 'feature:accounts', aliases: ['settings:write', 'settings:update', 'role:read', 'role:view'] },
  { key: 'operation-logs', label: '系统日志', group: '系统设置', path: '/settings/logs', permission: 'feature:settings', aliases: ['settings:read', 'settings:view'] },
  { key: 'timeout-reminder', label: '超时提醒', group: '超时提醒', path: '/timeout-reminder', permission: 'feature:timeout-reminder', aliases: ['settings:write', 'settings:update', 'rule:read', 'rule:view'] },
  { key: 'timeout-settings', label: '超时设置', group: '超时提醒', path: '/timeout-reminder/settings', permission: 'feature:timeout-reminder', aliases: ['settings:write', 'settings:update', 'rule:read', 'rule:view'] },
  { key: 'timeout-records', label: '超时记录', group: '超时提醒', path: '/timeout-reminder/records', permission: 'feature:timeout-reminder', aliases: ['settings:write', 'settings:update', 'rule:read', 'rule:view'] },
  { key: 'timeout-data', label: '超时数据', group: '超时提醒', path: '/timeout-reminder/data', permission: 'feature:timeout-reminder', aliases: ['settings:write', 'settings:update', 'rule:read', 'rule:view'] },
  { key: 'timeout-data-detail', label: '超时数据明细', group: '超时提醒', path: '/timeout-reminder/data/detail', permission: 'feature:timeout-reminder', aliases: ['settings:write', 'settings:update', 'rule:read', 'rule:view'] },
  { key: 'timeout-data-single', label: '单聊数据明细', group: '超时提醒', path: '/timeout-reminder/data/single', permission: 'feature:timeout-reminder', aliases: ['settings:write', 'settings:update', 'rule:read', 'rule:view'] },
  { key: 'timeout-data-group', label: '群聊数据明细', group: '超时提醒', path: '/timeout-reminder/data/group', permission: 'feature:timeout-reminder', aliases: ['settings:write', 'settings:update', 'rule:read', 'rule:view'] },
  { key: 'timeout-reminder-legacy', label: '超时提醒', group: '超时提醒', path: '/settings/timeout-reminder', permission: 'feature:timeout-reminder', aliases: ['settings:write', 'settings:update', 'rule:read', 'rule:view'] },
];

export const FEATURE_PERMISSION_GROUPS = Array.from(new Set(FEATURE_PERMISSIONS.map((item) => item.group)));

export const FEATURE_PERMISSION_BY_KEY = FEATURE_PERMISSIONS.reduce<Record<string, FeaturePermission>>((acc, item) => {
  acc[item.key] = item;
  return acc;
}, {});

export const FEATURE_PERMISSION_BY_PATH = FEATURE_PERMISSIONS.reduce<Record<string, FeaturePermission>>((acc, item) => {
  acc[item.path] = item;
  return acc;
}, {});

export const hasFeaturePermission = (
  permissions: string[] | undefined | null,
  feature: FeaturePermission | undefined,
): boolean => {
  if (!feature) return true;
  if (!permissions) return false;
  if (permissions.includes('*')) return true;
  return [feature.permission, ...(feature.aliases || [])].some((permission) => permissions.includes(permission));
};

export interface FeaturePermissionUser {
  is_superuser?: boolean | null;
  role_code?: string | null;
  permissions?: string[] | null;
}

export const isSystemRole = (user: FeaturePermissionUser | undefined | null): boolean => (
  user?.role_code === 'creator' || user?.role_code === 'super_admin'
);

export const hasFullFeatureAccess = (user: FeaturePermissionUser | undefined | null): boolean => {
  if (!user) return false;
  return (
    isSystemRole(user) ||
    Boolean(user.is_superuser) ||
    Boolean(user.permissions?.includes('*'))
  );
};

export const canOpenFeature = (
  user: FeaturePermissionUser | undefined | null,
  feature: FeaturePermission | undefined,
): boolean => {
  if (feature?.group === '系统设置') return isSystemRole(user);
  if (hasFullFeatureAccess(user)) return true;
  return hasFeaturePermission(user?.permissions, feature);
};
